import { validate as isValidUuid } from "uuid";

/**
 * Opt-in attributes for surfaces that aren't data grids (detail panels,
 * dialogs, summary cards). Put `data-copy-id` on the block that represents the
 * record; add `data-copy-id-type` only when the record isn't in the Apollo
 * cache under that id, or when the GraphQL `__typename` would be misleading.
 */
export const COPY_ID_ATTRIBUTE = "data-copy-id";
export const COPY_ID_TYPE_ATTRIBUTE = "data-copy-id-type";

// MUI's DataGrid stamps every row with `data-id="<rowId>"`, so grids are
// pickable without any per-grid wiring.
const PICK_SELECTOR = `[${COPY_ID_ATTRIBUTE}], .MuiDataGrid-row[data-id]`;

export type PickTarget = {
  element: HTMLElement;
  id: string;
  /** GraphQL `__typename`, which matches the Prisma model name for most records. */
  type: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readEntityId(entry: Record<string, unknown>, cacheKey: string): string | null {
  const direct = entry.id ?? entry._id;
  if (typeof direct === "string" && direct.length > 0) return direct;

  // Entries whose id field wasn't part of any selection set still carry it in
  // the `Type:id` cache key. Types with custom `keyFields` serialize a JSON
  // object into that suffix instead, which the UUID check rejects.
  const separator = cacheKey.indexOf(":");
  if (separator === -1) return null;
  const suffix = cacheKey.slice(separator + 1);
  return isValidUuid(suffix) ? suffix : null;
}

/**
 * Invert an Apollo cache snapshot into `id -> __typename`.
 *
 * Oak runs default `InMemoryCache` normalization, so anything rendered from a
 * GraphQL query is already stored under a `Facility:<uuid>`-style key. That
 * makes the owning model of an on-screen id a local read — resolving it over
 * the wire would mean a `lookupRecordByUuid` round trip per hover, and that
 * resolver falls back to scanning ~29 tables.
 *
 * When one id carries two typenames (a view type projected over the same row),
 * the lexicographically smaller name wins. The choice is arbitrary but stable:
 * `extract()` key order reflects cache insertion, so picking whichever came
 * first would label the same record differently between sessions.
 */
export function buildIdTypeIndex(snapshot: Record<string, unknown>): Map<string, string> {
  const index = new Map<string, string>();
  for (const [cacheKey, entry] of Object.entries(snapshot)) {
    if (!isRecord(entry)) continue;
    const typename = entry.__typename;
    if (typeof typename !== "string" || typename.length === 0) continue;
    const id = readEntityId(entry, cacheKey);
    if (id === null) continue;
    const existing = index.get(id);
    if (existing !== undefined && existing <= typename) continue;
    index.set(id, typename);
  }
  return index;
}

const UUID_SCAN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

/**
 * Pull the record id out of a grid row key.
 *
 * Plenty of grids key rows on a composite (`${facilityId}-${epaId}`) or on a
 * purely synthetic value (`row.month`, `${state}:${name}`). Composites still
 * name a real record, so scan out the embedded ids and prefer the leftmost one
 * the cache can identify — that biases toward the row's subject rather than an
 * incidental foreign key, and the hover chip shows which id won before the
 * click commits to it.
 */
function extractRecordId(rawId: string, typeIndex: ReadonlyMap<string, string>): string | null {
  if (isValidUuid(rawId)) return rawId;
  const candidates = (rawId.match(UUID_SCAN) ?? []).filter(isValidUuid);
  if (candidates.length === 0) return null;
  return candidates.find((candidate) => typeIndex.has(candidate)) ?? candidates[0] ?? null;
}

function readTarget(
  element: HTMLElement,
  typeIndex: ReadonlyMap<string, string>
): PickTarget | null {
  const explicitId = element.getAttribute(COPY_ID_ATTRIBUTE);
  if (explicitId !== null && explicitId.length > 0) {
    const explicitType = element.getAttribute(COPY_ID_TYPE_ATTRIBUTE);
    return {
      element,
      id: explicitId,
      type: explicitType ?? typeIndex.get(explicitId) ?? null,
    };
  }

  const rowId = element.getAttribute("data-id");
  if (rowId === null) return null;
  const recordId = extractRecordId(rowId, typeIndex);
  if (recordId === null) return null;
  return { element, id: recordId, type: typeIndex.get(recordId) ?? null };
}

/**
 * Walk up from the hovered node to the nearest element carrying a usable id.
 *
 * Keeps climbing past matches that turn out to be unusable (a grid row keyed on
 * a synthetic value) so an opted-in ancestor further up still wins.
 */
export function resolvePickTarget(
  from: EventTarget | null,
  typeIndex: ReadonlyMap<string, string>
): PickTarget | null {
  let current: Element | null = from instanceof Element ? from : null;
  while (current !== null) {
    const match = current.closest(PICK_SELECTOR);
    if (!(match instanceof HTMLElement)) return null;
    const resolved = readTarget(match, typeIndex);
    if (resolved !== null) return resolved;
    current = match.parentElement;
  }
  return null;
}
